import {
  Filter,
  Globe2,
  Grape,
  MapPin,
  Plus,
  Search,
  SlidersHorizontal,
  Wine as WineIcon,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "../components/AppShell";
import { LocationSelect } from "../components/LocationSelect";
import { EmptyState, ErrorState, PageLoader } from "../components/States";
import { WineCard } from "../components/WineCard";
import { useToast } from "../components/Toast";
import { api, OFFLINE_DATA_CHANGED_EVENT } from "../lib/api";
import {
  DRINK_NOW_STATUS,
  EMPTY_INVENTORY_FILTERS,
  inventoryFiltersFromLocation,
  inventoryPath,
  type InventoryFilters,
  type InventoryPathname,
  type TastingFilter,
} from "../lib/inventory-navigation";
import { Link, useLocation, useNavigate } from "../lib/router";
import {
  activeCoravins,
  currentYear,
  getTastingStatus,
  normalizedKey,
  wineLocationStocks,
} from "../lib/wine";
import {
  CATEGORIES,
  type CellarLocation,
  type CellarPlaceRecord,
  type TastingStatus,
  type Wine,
} from "../types";

type StockView = "en_stock" | "epuises";
const ORSAY_PLACE_ID = "lieu-orsay";
const HOULGATE_PLACE_ID = "lieu-houlgate";

function compareWineNames(a: Wine, b: Wine): number {
  const byName = `${a.producteur} ${a.nom}`.localeCompare(
    `${b.producteur} ${b.nom}`,
    "fr",
    { sensitivity: "base" },
  );
  return byName || a.id.localeCompare(b.id, "fr", { sensitivity: "base" });
}

function compareWineDeadlines(a: Wine, b: Wine): number {
  if (a.fin_deg == null && b.fin_deg == null) return compareWineNames(a, b);
  if (a.fin_deg == null) return 1;
  if (b.fin_deg == null) return -1;
  return a.fin_deg - b.fin_deg || compareWineNames(a, b);
}

function stockForLocationFilter(
  wine: Wine,
  filter: string,
  places: readonly CellarPlaceRecord[],
): number | undefined {
  if (!filter) return undefined;
  const stocks = wineLocationStocks(wine);
  if (filter.startsWith("lieu:")) {
    const placeId = filter.slice("lieu:".length);
    const placeName = places.find((place) => place.id === placeId)?.nom;
    return stocks.reduce(
      (total, item) =>
        item.lieu_id === placeId ||
        (!item.lieu_id && normalizedKey(item.lieu) === normalizedKey(placeName))
          ? total + item.stock
          : total,
      0,
    );
  }
  return stocks.reduce(
    (total, item) =>
      item.emplacement_id === filter ? total + item.stock : total,
    0,
  );
}

function stockAtPlace(
  wines: readonly Wine[],
  placeId: string,
  placeName: string,
): number {
  return wines.reduce(
    (wineTotal, wine) =>
      wineTotal +
      wineLocationStocks(wine).reduce(
        (locationTotal, item) =>
          (item.lieu_id
            ? item.lieu_id === placeId
            : normalizedKey(item.lieu) === normalizedKey(placeName))
            ? locationTotal + item.stock
            : locationTotal,
        0,
      ),
    0,
  );
}

function coravinCountForLocationFilter(
  wine: Wine,
  filter: string,
  places: readonly CellarPlaceRecord[],
): number {
  const bottles = activeCoravins(wine);
  if (!filter) return bottles.length;
  if (!filter.startsWith("lieu:")) {
    return bottles.filter((bottle) => bottle.emplacement_id === filter).length;
  }
  const placeId = filter.slice("lieu:".length);
  const placeName = places.find((place) => place.id === placeId)?.nom;
  return bottles.filter((bottle) =>
    bottle.lieu_id === placeId ||
    (!bottle.lieu_id && Boolean(placeName) && normalizedKey(bottle.lieu) === normalizedKey(placeName)),
  ).length;
}

export function InventoryPage() {
  const year = currentYear();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const inventoryPathname: InventoryPathname =
    pathname === "/epuises" ? "/epuises" : "/";
  const filters = useMemo(
    () => inventoryFiltersFromLocation(inventoryPathname, search),
    [inventoryPathname, search],
  );
  const { query, category, location, tasting, region, coravin } = filters;
  const [wines, setWines] = useState<Wine[]>([]);
  const [locations, setLocations] = useState<CellarLocation[]>([]);
  const [places, setPlaces] = useState<CellarPlaceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const view: StockView = pathname === "/epuises" ? "epuises" : "en_stock";
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  const updateFilters = useCallback((
    updates: Partial<InventoryFilters>,
    targetPathname: InventoryPathname = inventoryPathname,
  ) => {
    navigate(
      inventoryPath(targetPathname, { ...filters, ...updates }),
      { replace: true, preventScrollReset: true },
    );
  }, [filters, inventoryPathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [available, exhausted, locationValues, placeValues] = await Promise.all([
        api.listWines("en_stock"),
        api.listWines("epuises"),
        api.listLocations(),
        api.listPlaces(),
      ]);
      const byId = new Map<string, Wine>();
      [...available, ...exhausted].forEach((wine) => byId.set(wine.id, wine));
      setWines([...byId.values()]);
      setLocations(locationValues);
      setPlaces(placeValues);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const refreshAfterSynchronization = () => void load();
    window.addEventListener(
      OFFLINE_DATA_CHANGED_EVENT,
      refreshAfterSynchronization,
    );
    return () =>
      window.removeEventListener(
        OFFLINE_DATA_CHANGED_EVENT,
        refreshAfterSynchronization,
      );
  }, [load]);

  useEffect(() => {
    const normalizedPath = inventoryPath(inventoryPathname, filters);
    if (normalizedPath === `${pathname}${search}`) return;
    navigate(normalizedPath, { replace: true, preventScrollReset: true });
  }, [filters, inventoryPathname, navigate, pathname, search]);

  const stocked = useMemo(() => wines.filter((wine) => wine.stock > 0), [wines]);
  const regionOptions = useMemo(() => {
    const values = new Map<string, string>();
    stocked.forEach((wine) => {
      const value = wine.region?.trim();
      if (!value) return;
      const key = normalizedKey(value);
      if (!values.has(key)) values.set(key, value);
    });
    return [...values.values()].sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base" }),
    );
  }, [stocked]);
  const cellarSummary = useMemo(() => {
    const orsayPlace =
      places.find((item) => item.id === ORSAY_PLACE_ID) ??
      places.find((item) => normalizedKey(item.nom) === normalizedKey("Orsay"));
    const orsayPlaceId = orsayPlace?.id ?? ORSAY_PLACE_ID;
    const orsayPlaceName = orsayPlace?.nom ?? "Orsay";
    const houlgatePlace =
      places.find((item) => item.id === HOULGATE_PLACE_ID) ??
      places.find(
        (item) => normalizedKey(item.nom) === normalizedKey("Houlgate"),
      );
    const houlgatePlaceId = houlgatePlace?.id ?? HOULGATE_PLACE_ID;
    const houlgatePlaceName = houlgatePlace?.nom ?? "Houlgate";
    const drinkNowWines = stocked.filter(
      (wine) => getTastingStatus(wine, year) === DRINK_NOW_STATUS,
    );
    const orsayDrinkNowBottles = stockAtPlace(
      drinkNowWines,
      orsayPlaceId,
      orsayPlaceName,
    );
    const houlgateDrinkNowBottles = stockAtPlace(
      drinkNowWines,
      houlgatePlaceId,
      houlgatePlaceName,
    );

    return {
      orsayDrinkNowBottles,
      orsayFilter: orsayPlace ? `lieu:${orsayPlaceId}` : "",
      houlgateDrinkNowBottles,
      houlgateFilter: houlgatePlace ? `lieu:${houlgatePlaceId}` : "",
    };
  }, [places, stocked, year]);
  const visibleWines = useMemo(() => {
    const search = normalizedKey(query);
    const activeView = wines.filter((wine) =>
      view === "en_stock" ? wine.stock > 0 : wine.stock === 0,
    );
    const filtered = activeView.filter((wine) => {
      const haystack = normalizedKey(
        [
          wine.producteur,
          wine.nom,
          wine.appellation,
          wine.region,
          wine.cepages,
          wine.millesime ?? "NM",
          ...wineLocationStocks(wine).flatMap((item) => [
            item.emplacement,
            item.emplacement_nom,
            item.lieu,
          ]),
        ].join(" "),
      );
      const status = getTastingStatus(wine, year);
      const tastingMatches = !tasting || status === tasting;
      return (
        (!search || haystack.includes(search)) &&
        (!category || wine.categorie === category) &&
        (!location || (stockForLocationFilter(wine, location, places) ?? 0) > 0) &&
        (!region || normalizedKey(wine.region) === normalizedKey(region)) &&
        (!coravin || coravinCountForLocationFilter(wine, location, places) > 0) &&
        tastingMatches
      );
    });

    return filtered.sort(compareWineDeadlines);
  }, [category, location, places, query, region, tasting, coravin, view, wines, year]);
  const visibleBottleCount = useMemo(
    () =>
      visibleWines.reduce(
        (total, wine) =>
          total +
          (coravin
            ? coravinCountForLocationFilter(wine, location, places)
            : (stockForLocationFilter(wine, location, places) ?? wine.stock)),
        0,
      ),
    [coravin, location, places, visibleWines],
  );

  const activeFilterCount = [category, location, tasting, region, coravin].filter(Boolean).length;
  const hasSearchOrFilters = Boolean(query || activeFilterCount);

  const resetFilters = () => {
    updateFilters(EMPTY_INVENTORY_FILTERS);
  };

  const handleTastingChange = (nextTasting: TastingFilter) => {
    updateFilters({ tasting: nextTasting });
  };

  const orsayShortcutActive =
    view === "en_stock" &&
    Boolean(cellarSummary.orsayFilter) &&
    location === cellarSummary.orsayFilter &&
    tasting === DRINK_NOW_STATUS;
  const houlgateShortcutActive =
    view === "en_stock" &&
    Boolean(cellarSummary.houlgateFilter) &&
    location === cellarSummary.houlgateFilter &&
    tasting === DRINK_NOW_STATUS;

  const handleOrsayShortcut = () => {
    if (orsayShortcutActive) {
      updateFilters({ location: "", tasting: "" });
      return;
    }

    updateFilters({
      query: "",
      category: "",
      region: "",
      location: cellarSummary.orsayFilter,
      tasting: DRINK_NOW_STATUS,
    }, pathname === "/epuises" ? "/" : inventoryPathname);
  };

  const handleHoulgateShortcut = () => {
    if (houlgateShortcutActive) {
      updateFilters({ location: "", tasting: "" });
      return;
    }

    updateFilters({
      query: "",
      category: "",
      region: "",
      location: cellarSummary.houlgateFilter,
      tasting: DRINK_NOW_STATUS,
    }, pathname === "/epuises" ? "/" : inventoryPathname);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.downloadAllExports();
      showToast({ message: "Les deux fichiers CSV ont été téléchargés." });
    } catch (caught) {
      showToast({
        message: caught instanceof Error ? caught.message : "Export impossible.",
        tone: "info",
      });
    } finally {
      setExporting(false);
    }
  };
  const summaryUnavailable = loading || Boolean(error);

  return (
    <AppShell onExport={handleExport} exporting={exporting}>
      <div className="page inventory-page">
        <section className="inventory-hero" aria-labelledby="inventory-title">
          <h1 id="inventory-title" className="sr-only">Inventaire</h1>
          <div className="summary-cards" role="group" aria-label="Filtres rapides de la cave">
            <button
              className="summary-card summary-action summary-shortcut summary-location-orsay"
              type="button"
              aria-pressed={orsayShortcutActive}
              aria-label={
                summaryUnavailable || !cellarSummary.orsayFilter
                  ? "Afficher les bouteilles à boire dès maintenant à Orsay"
                  : `Afficher les ${cellarSummary.orsayDrinkNowBottles} bouteille${cellarSummary.orsayDrinkNowBottles === 1 ? "" : "s"} à boire dès maintenant à Orsay`
              }
              disabled={summaryUnavailable || !cellarSummary.orsayFilter}
              onClick={handleOrsayShortcut}
            >
              <span className="summary-place">Orsay</span>
              <span className="summary-metric">
                <strong>{summaryUnavailable || !cellarSummary.orsayFilter ? "—" : cellarSummary.orsayDrinkNowBottles}</strong>
                <WineIcon size={18} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <span className="summary-detail">à boire maintenant</span>
            </button>
            <button
              className="summary-card summary-action summary-shortcut summary-location-houlgate"
              type="button"
              aria-pressed={houlgateShortcutActive}
              aria-label={
                summaryUnavailable || !cellarSummary.houlgateFilter
                  ? "Afficher les bouteilles à boire dès maintenant à Houlgate"
                  : `Afficher les ${cellarSummary.houlgateDrinkNowBottles} bouteille${cellarSummary.houlgateDrinkNowBottles === 1 ? "" : "s"} à boire dès maintenant à Houlgate`
              }
              disabled={summaryUnavailable || !cellarSummary.houlgateFilter}
              onClick={handleHoulgateShortcut}
            >
              <span className="summary-place">Houlgate</span>
              <span className="summary-metric">
                <strong>{summaryUnavailable || !cellarSummary.houlgateFilter ? "—" : cellarSummary.houlgateDrinkNowBottles}</strong>
                <WineIcon size={18} strokeWidth={1.8} aria-hidden="true" />
              </span>
              <span className="summary-detail">à boire maintenant</span>
            </button>
          </div>
        </section>

        <section className="inventory-controls" aria-label="Recherche et filtres">
          <div className="search-row">
            <label className="search-box">
              <Search size={19} aria-hidden="true" />
              <span className="sr-only">Rechercher dans la vue active</span>
              <input
                type="search"
                value={query}
                onChange={(event) => updateFilters({ query: event.target.value })}
                placeholder={view === "en_stock" ? "Rechercher dans les vins en stock…" : "Rechercher dans les vins épuisés…"}
              />
              {query ? (
                <button type="button" onClick={() => updateFilters({ query: "" })} aria-label="Effacer la recherche">
                  <X size={17} aria-hidden="true" />
                </button>
              ) : null}
            </label>
            <button
              className={`button button-filter ${activeFilterCount ? "has-filter" : ""}`}
              type="button"
              aria-expanded={showFilters}
              aria-controls="inventory-filters"
              onClick={() => setShowFilters((value) => !value)}
            >
              <SlidersHorizontal size={18} aria-hidden="true" />
              Filtres
              {activeFilterCount ? <span>{activeFilterCount}</span> : null}
            </button>
          </div>

          <div
            id="inventory-filters"
            className={`filter-panel ${showFilters ? "filter-panel-open" : ""}`}
          >
            <label>
              <span><Filter size={15} aria-hidden="true" /> Catégorie</span>
              <select value={category} onChange={(event) => updateFilters({ category: event.target.value })}>
                <option value="">Toutes</option>
                {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label>
              <span><MapPin size={15} aria-hidden="true" /> Emplacement</span>
              <LocationSelect
                locations={locations}
                places={places}
                includePlaceOptions
                value={location}
                onChange={(value) => updateFilters({ location: value })}
                emptyLabel="Tous les emplacements"
              />
            </label>
            <label>
              <span><Grape size={15} aria-hidden="true" /> Dégustation</span>
              <select
                value={tasting}
                onChange={(event) =>
                  handleTastingChange(event.target.value as TastingFilter)
                }
              >
                <option value="">Tous les statuts</option>
                {([
                  DRINK_NOW_STATUS,
                  "À garder",
                  "Non renseigné",
                ] as TastingStatus[]).map(
                  (item) => <option key={item}>{item}</option>,
                )}
              </select>
            </label>
            <label>
              <span><Globe2 size={15} aria-hidden="true" /> Région</span>
              <select value={region} onChange={(event) => updateFilters({ region: event.target.value })}>
                <option value="">Toutes les régions</option>
                {regionOptions.map((item) => <option key={normalizedKey(item)}>{item}</option>)}
              </select>
            </label>
            {view === "en_stock" ? (
              <label className="coravin-filter">
                <span><WineIcon size={15} aria-hidden="true" /> Coravin</span>
                <select value={coravin ? "1" : ""} onChange={(event) => updateFilters({ coravin: event.target.value === "1" })}>
                  <option value="">Tous les vins</option>
                  <option value="1">Coravinés</option>
                </select>
              </label>
            ) : null}
            {activeFilterCount ? (
              <button className="clear-filters" type="button" onClick={resetFilters}>
                <X size={15} aria-hidden="true" /> Tout effacer
              </button>
            ) : null}
          </div>
        </section>

        {loading ? <PageLoader /> : null}
        {!loading && error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
        {!loading && !error ? (
          <section className="inventory-results" aria-live="polite">
            <div className="results-heading">
              <p>
                <strong>{visibleBottleCount}</strong> bouteille{visibleBottleCount === 1 ? "" : "s"}
                {coravin ? ` coravinée${visibleBottleCount === 1 ? "" : "s"}` : ""}
                {hasSearchOrFilters ? " dans cette vue" : ""}
              </p>
              {hasSearchOrFilters ? (
                <button type="button" onClick={resetFilters}>Réinitialiser</button>
              ) : null}
            </div>
            {visibleWines.length ? (
              <div className="wine-list">
                {visibleWines.map((wine) => (
                  <WineCard
                    key={wine.id}
                    wine={wine}
                    filteredStock={stockForLocationFilter(wine, location, places)}
                    filteredCoravinCount={coravinCountForLocationFilter(wine, location, places)}
                    inventoryReturnTo={inventoryPath(inventoryPathname, filters)}
                  />
                ))}
              </div>
            ) : hasSearchOrFilters ? (
              <EmptyState
                icon={<Search size={27} aria-hidden="true" />}
                title="Aucun vin ne correspond"
                message={`Modifiez votre recherche ou vos filtres. Les vins ${view === "en_stock" ? "épuisés" : "en stock"} restent dans leur vue dédiée.`}
                action={<button className="button button-secondary" type="button" onClick={resetFilters}>Effacer les filtres</button>}
              />
            ) : view === "en_stock" ? (
              <EmptyState
                title="Votre cave attend sa première bouteille"
                message="Ajoutez un vin pour commencer votre inventaire. Les informations essentielles suffisent."
                action={<Link className="button button-primary empty-inline-add" to="/vins/nouveau"><Plus size={18} /> Ajouter un vin</Link>}
              />
            ) : (
              <EmptyState
                title="Aucune référence épuisée"
                message="Les vins dont le stock atteint zéro apparaîtront ici, avec leur historique intact."
              />
            )}
          </section>
        ) : null}
      </div>
    </AppShell>
  );
}
